import apiUrl from '../../../constants';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import VueElementLoading from 'vue-element-loading';
import Paginate from 'vuejs-paginate';
import moment from 'moment';

const HOLD_CAPACITY_COLUMNS = [
  { key: 'hubname',           header: 'Hub Name',           required: true  },
  { key: 'hold_area_capacity', header: 'Hold Area Capacity', required: true  },
  { key: 'ptl_capacity',      header: 'PTL Capacity',       required: true },
];

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  for (const col of HOLD_CAPACITY_COLUMNS.filter((c) => c.required)) {
    if (!headers.includes(col.key)) throw new Error(`Missing required column: ${col.key}`);
  }
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = line.split(',').map((v) => v.trim());
    const row = {};
    headers.forEach((h, idx) => { row[h] = values[idx] !== undefined ? values[idx] : ''; });
    const hubname = row['hubname'] || '';
    const hold_area_capacity = parseInt(row['hold_area_capacity'], 10);
    if (!hubname) throw new Error(`Row ${i}: hubname is required`);
    if (isNaN(hold_area_capacity) || hold_area_capacity < 1)
      throw new Error(`Row ${i}: hold_area_capacity must be a positive integer`);
    const ptl_capacity = parseInt(row['ptl_capacity'], 10);
    if (!row['ptl_capacity'] || isNaN(ptl_capacity) || ptl_capacity < 1)
      throw new Error(`Row ${i}: ptl_capacity must be a positive integer`);
    rows.push({ hubname, hold_area_capacity, ptl_capacity });
  }
  if (rows.length === 0) throw new Error('No valid data rows found in CSV');
  return rows;
}

export default {
  name: 'hold-capacity-config',
  components: { VueElementLoading, Paginate },
  data() {
    return {
      hubId: '',
      createdBy: '',
      currentTab: 'upload',
      tabs: [
        { key: 'upload',  value: 'Upload Config' },
        { key: 'history', value: 'History'       },
      ],
      mappingColumns: HOLD_CAPACITY_COLUMNS,

      // upload tab
      isLoadingUpload: false,
      selectedFile: null,
      parseError: '',
      parsedRows: [],
      lastUploadSummary: null,
      failed: [],

      // mappings table
      isSearching: false,
      mappings: [],
      pageno: 0,
      limit: 10,
      totalRows: 0,
      pageCount: 0,

      // history tab
      isLoadingHistory: false,
      historyRows: [],
      historyPageno: 0,
      historyLimit: 10,
      historyTotalRows: 0,
      historyPageCount: 0,
    };
  },
  filters: {
    // HUB-8058: API timestamps are stored in GMT — show in IST.
    momentIst(date) {
      if (!date) return '--';
      const m = moment.utc(date);
      return m.isValid() ? m.utcOffset(330).format('DD-MM-YYYY HH:mm:ss') : date;
    },
  },
  mounted() {
    const enc = window.localStorage.getItem('userdetail');
    const bytes = CryptoJS.AES.decrypt(enc.toString(), 'Key');
    const userdetail = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    this.createdBy = userdetail[0].email;
    this.hubId = userdetail[0].HubId[0];
    this.searchMappings();
  },
  methods: {
    secondFunction(tab) {
      this.currentTab = tab.key;
      if (tab.key === 'history') {
        this.historyPageno = 0;
        this.getAuditHistory();
        this.lastUploadSummary = null;
      }
    },

    // ---------- file handling ----------
    onFileChange(e) {
      this.parseError = '';
      this.parsedRows = [];
      const f = e.target.files[0];
      if (!f) return;
      if (!f.name.endsWith('.csv')) { this.parseError = 'Only .csv files are accepted'; return; }
      if (f.size > 5242880) { this.parseError = 'Max file size is 5 MB'; return; }
      this.selectedFile = f;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          this.parsedRows = parseCSV(ev.target.result);
        } catch (err) {
          this.parsedRows = [];
          this.parseError = err.message;
        }
      };
      reader.readAsText(f);
    },

    resetForm(event, preserveSummary = false) {
      this.isLoadingUpload = false;
      this.failed = [];
      this.selectedFile = null;
      this.parseError = '';
      this.parsedRows = [];
      if (!preserveSummary) this.lastUploadSummary = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      if (event && event.target && event.target.form) event.target.form.reset();
    },

    downloadSampleCsv() {
      const csv = 'hubname,hold_area_capacity,ptl_capacity\nPNQ/CHK,10000,30\nDEL/PC2,1000,15\n';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'hold_capacity_sample.csv'; a.click();
      URL.revokeObjectURL(url);
    },

    // ---------- upload ----------
    onUploadSubmit() {
      if (!this.parsedRows.length || this.parseError || this.isLoadingUpload) return;
      this.isLoadingUpload = true;
      this.lastUploadSummary = null;
      this.failed = [];
      axios.post(apiUrl.api_url_hubops + 'uploadHoldCapacityConfig', {
        hubs: this.parsedRows,
        uploadedby: this.createdBy,
      }, { headers: { Authorization: 'authkey xB84JJ89Hd25' } })
        .then((result) => {
          if (result.data.ResultCode == 100) {
            const data = result.data.ResponseData || {};
            this.lastUploadSummary = {
              totalRows:      data.totalRows      != null ? data.totalRows      : null,
              validCount:     data.validCount     != null ? data.validCount     : null,
              insertedCount:  data.insertedCount  != null ? data.insertedCount  : null,
              updatedCount:   data.updatedCount   != null ? data.updatedCount   : null,
            };
            const results = (data.results) || [];
            if (results.length) this.failed = results.filter((r) => !r.success);
            this.$alertify.success(result.data.ReturnMessage || 'Upload complete.');
            this.resetForm(null, true);
            this.pageno = 0;
            this.searchMappings();
          } else {
            this.$alertify.error(result.data.ReturnMessage);
          }
        })
        .catch(() => { this.$alertify.error('Error Occurred'); })
        .finally(() => { this.isLoadingUpload = false; });
    },

    exportFailedConfig() {
      if (!this.failed.length) { this.$alertify.error('No failed rows to export.'); return; }
      const headers = ['hubname', 'hold_area_capacity', 'ptl_capacity', 'reason'];
      const lines = [
        headers.join(','),
        ...this.failed.map((r) => headers.map((h) => r[h] != null ? r[h] : '').join(',')),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'hold_capacity_failed.csv'; a.click();
      URL.revokeObjectURL(url);
    },

    // ---------- mappings table ----------
    searchMappings() {
      this.isSearching = true;
      axios.get(apiUrl.api_url_hubops + 'holdCapacityConfig', {
        params: { limit: this.limit, offset: this.pageno * this.limit },
        headers: { Authorization: 'authkey xB84JJ89Hd25' },
      }).then((res) => {
        if (res.data.ResultCode == 100) {
          this.mappings = res.data.ResponseData || [];
          const pg = res.data.pagination || {};
          this.totalRows = pg.totalCount || this.mappings.length;
          this.pageCount = pg.totalPages || 0;
        } else {
          this.mappings = []; this.totalRows = 0; this.pageCount = 0;
          if (res.data.ResultCode != 101) this.$alertify.error(res.data.ReturnMessage);
        }
      }).catch(() => { this.$alertify.error('Error Occurred'); })
        .finally(() => { this.isSearching = false; });
    },

    getPaginationData(pageNum) {
      this.pageno = pageNum - 1;
      this.searchMappings();
    },

    // ---------- history tab ----------
    getAuditHistory() {
      this.isLoadingHistory = true;
      axios.get(apiUrl.api_url_hubops + 'holdCapacityConfigHistory', {
        params: { pageIndex: this.historyPageno, pageSize: this.historyLimit },
        headers: { Authorization: 'authkey xB84JJ89Hd25' },
      }).then((res) => {
        if (res.data.ResultCode == 100) {
          this.historyRows = res.data.ResponseData || [];
          const pg = res.data.pagination || {};
          this.historyTotalRows = pg.totalCount || this.historyRows.length;
          this.historyPageCount = pg.totalPages || 0;
        } else {
          this.historyRows = []; this.historyTotalRows = 0; this.historyPageCount = 0;
          if (res.data.ResultCode != 101) this.$alertify.error(res.data.ReturnMessage);
        }
      }).catch(() => { this.$alertify.error('Error Occurred'); })
        .finally(() => { this.isLoadingHistory = false; });
    },

    getHistoryPagination(pageNum) {
      this.historyPageno = pageNum - 1;
      this.getAuditHistory();
    },

    auditActionBadgeClass(action) {
      const key = (action || '').toLowerCase();
      if (key === 'insert' || key === 'uploaded')    return 'action-badge action-uploaded';
      if (key === 'update' || key === 'activated')   return 'action-badge action-activated';
      if (key === 'deactivated')                     return 'action-badge action-deactivated';
      return 'action-badge';
    },

    formatDate(val) {
      if (!val) return '-';
      const m = moment.utc(val);
      return m.isValid() ? m.utcOffset(330).format('DD MMM YYYY, HH:mm') : val;
    },
  },
};



// WEBPACK FOOTER //
// ./src/views/components/HoldCapacityConfig/HoldCapacityConfig.js